import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCustomerData } from '../hooks/useCustomerData';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface APIKey {
  id: string;
  name: string;
  key: string;
  created_at: string;
  last_used: string | null;
  is_active: boolean;
}

const Settings: React.FC = () => {
  const { currentUser, getIdToken } = useAuth();
  const { profile, refreshData } = useCustomerData();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        setApiKeys(Array.isArray(data) ? data : []);
      } else {
        setError('Failed to fetch API keys');
        // Set empty array if request fails
        setApiKeys([]);
      }
    } catch (error) {
      setError('Failed to fetch API keys');
      // Set empty array on error
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newKeyName,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('API key created successfully!');
        setNewKeyName('');
        setShowCreateForm(false);
        fetchApiKeys();
        // Show the new key temporarily
        setVisibleKeys(new Set([data.id]));
        setTimeout(() => setVisibleKeys(new Set()), 30000); // Hide after 30 seconds
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create API key');
      }
    } catch (error) {
      setError('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteApiKey = (keyId: string) => {
    setKeyToDelete(keyId);
    setShowDeleteModal(true);
  };

  const confirmDeleteApiKey = async () => {
    if (!keyToDelete) return;

    setDeleting(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/api-keys/${keyToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('API key deleted successfully!');
        fetchApiKeys();
        setShowDeleteModal(false);
        setKeyToDelete(null);
      } else {
        setError('Failed to delete API key');
      }
    } catch (error) {
      setError('Failed to delete API key');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteApiKey = () => {
    setShowDeleteModal(false);
    setKeyToDelete(null);
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setSuccess('API key copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to copy API key');
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    const newVisibleKeys = new Set(visibleKeys);
    if (newVisibleKeys.has(keyId)) {
      newVisibleKeys.delete(keyId);
    } else {
      newVisibleKeys.add(keyId);
    }
    setVisibleKeys(newVisibleKeys);
  };

  const maskApiKey = (key: string | undefined) => {
    if (!key || key.length <= 8) return key || '••••••••';
    return key.substring(0, 4) + '•'.repeat(key.length - 8) + key.substring(key.length - 4);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Account Settings</h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage your account settings and API keys
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-macos text-sm mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-macos text-sm mb-6">
          {success}
        </div>
      )}

      {/* Account Information */}
      <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={currentUser?.email || ''}
              disabled
              className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Plan
            </label>
            <input
              type="text"
              value={profile?.plan ? profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1) : 'Free'}
              disabled
              className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-gray-50 text-gray-500 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* API Keys Management */}
      <div className="bg-white rounded-macos-lg shadow-macos border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">API Keys</h3>
              <p className="mt-1 text-sm text-gray-600">
                Use API keys to authenticate requests to the Eddystone URL Shortener API
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create API Key
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Create API Key Form */}
          {showCreateForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-macos border border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Create New API Key</h4>
              <form onSubmit={handleCreateApiKey} className="space-y-4">
                <div>
                  <label htmlFor="key-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Key Name
                  </label>
                  <input
                    id="key-name"
                    type="text"
                    required
                    className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    style={{ backgroundColor: 'white', color: '#111827' }}
                    placeholder="e.g., Production API, Mobile App"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewKeyName('');
                      setError('');
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 text-sm font-medium rounded-macos text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 py-3 px-4 border border-transparent text-sm font-medium rounded-macos text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {creating ? 'Creating...' : 'Create Key'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* API Keys List */}
          {apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first API key to start using the API.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Array.isArray(apiKeys) && apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="border border-gray-200 rounded-macos p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{apiKey.name}</h4>
                        {!apiKey.is_active && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <code className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {apiKey.key ? (visibleKeys.has(apiKey.id) ? apiKey.key : maskApiKey(apiKey.key)) : '••••••••••••••••'}
                        </code>
                        {apiKey.key && (
                          <>
                            <button
                              onClick={() => toggleKeyVisibility(apiKey.id)}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                              title={visibleKeys.has(apiKey.id) ? 'Hide key' : 'Show key'}
                            >
                              {visibleKeys.has(apiKey.id) ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleCopyKey(apiKey.key)}
                              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                              title="Copy key"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {!apiKey.key && (
                          <span className="text-xs text-gray-500 italic">Key hidden for security</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Created {formatDate(apiKey.created_at)}</span>
                        {apiKey.last_used && (
                          <span>Last used {formatDate(apiKey.last_used)}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteApiKey(apiKey.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete API key"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* API Documentation Link */}
          <div className="mt-6 p-4 bg-blue-50 rounded-macos border border-blue-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  API Documentation
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Learn how to use your API keys to integrate with the Eddystone URL Shortener API.
                  </p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <a
                      href="/api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-50 px-2 py-1.5 rounded-md text-sm font-medium text-blue-800 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-50 focus:ring-blue-600"
                    >
                      View Documentation
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDeleteApiKey}
        onConfirm={confirmDeleteApiKey}
        title="Delete API Key"
        message="Are you sure you want to delete this API key? This action cannot be undone and will immediately revoke access for any applications using this key."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />
    </div>
  );
};

export default Settings;